import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import {
  RecentOfferListItem,
  type ApplicationStatusKey,
} from './RecentOfferListItem'
import { KpiInfoButton } from './KpiInfoButton'
import type { RecentOfferStatsItem } from 'lib/api'

interface DashboardRecentOffersCardProps {
  offers: RecentOfferStatsItem[]
  footerLabel: string
}

const applicationStatusOrder: ApplicationStatusKey[] = [
  'applied',
  'scanning',
  'screening',
  'interview',
  'hired',
  'rejected',
]

export function DashboardRecentOffersCard({
  offers,
  footerLabel,
}: DashboardRecentOffersCardProps) {
  const { t } = useTranslation()

  const statusLabels = applicationStatusOrder.reduce((labels, status) => {
    labels[status] = t(`status.${status}`, status)
    return labels
  }, {} as Record<ApplicationStatusKey, string>)

  return (
    <section
      className="box-border flex w-full min-w-0 flex-col gap-4 rounded-[6px] border border-border bg-card p-3 text-card-foreground sm:p-4 lg:gap-8"
      aria-label={t('dashboard.recruitmentStats.recentOffersTitle')}
    >
      <div className="flex w-full shrink-0 items-center gap-1 rounded-[6px] bg-card px-3 py-3 sm:px-4">
        <h2 className="min-w-0 text-[11px] font-medium uppercase leading-tight tracking-[0.06em] text-muted-foreground">
          {t('dashboard.recruitmentStats.recentOffersTitle')}
        </h2>
        <KpiInfoButton description={t('dashboard.recruitmentStats.recentOffersDescription')} />
      </div>

      <div className="flex min-h-0 w-full flex-1 flex-col gap-2 overflow-y-auto">
        {offers.length === 0 ? (
          <div className="flex min-h-[140px] items-center justify-center rounded-[6px] bg-muted/40 px-4 text-center text-sm text-muted-foreground">
            {t('dashboard.recruitmentStats.noOffersForRange', 'Aucune offre pour cette période')}
          </div>
        ) : (
          offers.map((offer, index) => (
            <RecentOfferListItem
              key={offer.id}
              id={offer.id}
              jobTitle={offer.job_title}
              byStatus={offer.by_status}
              statusLabels={statusLabels}
              mutedBackground={index % 2 === 1}
            />
          ))
        )}
      </div>

      <div className="flex w-full shrink-0 items-center justify-end gap-2">
        <Link
          to="/offers"
          className="inline-flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground sm:text-sm"
        >
          {t('dashboard.recruitmentStats.viewAllOffers', 'Voir toutes les offres')}
          <ChevronRight className="h-4 w-4 shrink-0" strokeWidth={2} />
        </Link>
      </div>
    </section>
  )
}
